import { ROLES, TRUST_ROLES, canPostJournals, hasAnyRole, type Role } from './auth.js';
import type { DashboardLayout } from './range.js';

/**
 * Who may do what to whom.
 *
 * Pure policy: no database, no session, no I/O. Every rule here is one a reviewer should be
 * able to read and agree with, and every one of them exists because getting it wrong either
 * locks an agency out of its own account or lets someone quietly promote themselves.
 */

// ── Organisation kinds ───────────────────────────────────────────────────────

export const ORG_KINDS = ['agency', 'platform'] as const;
export type OrgKind = (typeof ORG_KINDS)[number];

/**
 * Roles that only exist inside the platform organisation — our own staff.
 *
 * An agency can never grant these to itself: `support` arrives only as a time-boxed grant
 * issued from the platform org, and `platform_admin` is meaningless outside it.
 */
export const PLATFORM_ONLY_ROLES: readonly Role[] = ['platform_admin', 'support'];

/**
 * True when these roles belong to ProManage's own staff rather than to the agency.
 *
 * An agency cannot reach this state by any route it controls: `platform_admin` is only
 * assignable inside the platform organisation, and `support` arrives in an agency ONLY through
 * `platform_grant_support_access`, a database function granted to nobody else. That is what
 * makes this predicate safe to key a privilege decision on — a customer cannot award it to
 * themselves in order to acquire whatever it unlocks.
 */
export function isPlatformStaff(roles: readonly string[]): boolean {
  return hasAnyRole(roles, PLATFORM_ONLY_ROLES);
}

/** Roles an agency's own administrator may hand out. */
export const AGENCY_ASSIGNABLE_ROLES: readonly Role[] = [
  'property_manager',
  'letting_agent',
  'business_development_manager',
  'inspection_manager',
  'accounts',
  'trust_admin',
  'org_admin',
];

/** Roles assignable inside the platform organisation. */
export const PLATFORM_ASSIGNABLE_ROLES: readonly Role[] = ['platform_admin', 'support'];

export function assignableRoles(kind: OrgKind): readonly Role[] {
  return kind === 'platform' ? PLATFORM_ASSIGNABLE_ROLES : AGENCY_ASSIGNABLE_ROLES;
}

/**
 * Agency job roles that may be named as responsible for a property.
 *
 * A JOB TITLE, NOT A PERMISSION. Being on this list says an agency recognises the person as
 * someone a property can be put in the name of; it grants nothing. `letting_agent`,
 * `business_development_manager` and `inspection_manager` are deliberately absent from
 * `PROPERTY_ROLES` below — they can be assigned, and until one of them is also given
 * `property_manager` they cannot maintain the property book. That separation is the point: an
 * agency should be able to record who is responsible for a house without that record being the
 * thing that hands out write access.
 *
 * `0056_property_managers.sql` enforces the same list in the database, and `property.test.ts`
 * asserts the two have not drifted apart.
 */
export const PROPERTY_MANAGER_ROLES: readonly Role[] = [
  'property_manager',
  'letting_agent',
  'business_development_manager',
  'inspection_manager',
];

export function canBeAssignedToProperty(roles: readonly string[]): boolean {
  return hasAnyRole(roles, PROPERTY_MANAGER_ROLES);
}

// ── Capabilities ─────────────────────────────────────────────────────────────

/**
 * May author, edit, enable or disable an automation rule — `org_admin`, and nothing less.
 *
 * AUTHORING IS CLOSE TO A PERMISSION GRANT, which is docs/AUTOMATION.md §11's first open question
 * and the reason this is not `canManageProperties`. A rule that emails every owner is a mailing
 * capability; a rule that flags every payee change is a control somebody else relies on. Neither
 * looks like a permission on the form that creates it, and both behave like one afterwards.
 *
 * DELIBERATELY NOT A TRUST ROLE EITHER, and that is the other half of the argument. No action on
 * the catalogue moves money (§6) — the whole list is non-financial or a proposal a person
 * executes — so requiring `trust_admin` would say automation is a money authority when the design
 * is built precisely so that it is not. It is an administrative one: who may make the software act
 * on this agency's behalf.
 *
 * WRITTEN SEPARATELY FROM `canManageUsers`, which it resolves to today, for the reason
 * `canTransferCommission` is separate from `canApproveDisbursements`: they are two different
 * arguments about who holds an authority, and an agency that ever wants an operations manager who
 * writes rules but does not invite staff should find two rules to change rather than one predicate
 * used twice. §11 is still open on how agencies actually divide this; when it is answered, this is
 * the one function that moves.
 */
export function canAuthorAutomations(roles: readonly string[]): boolean {
  return roles.includes('org_admin') || roles.includes('platform_admin');
}

/**
 * May change the agency's brand kit — the logo, typeface, colour and footer its emails carry.
 *
 * AN ADMINISTRATIVE AUTHORITY, for `canAuthorAutomations`'s reason: nothing here moves money, but
 * the footer is appended to every message the agency sends, which makes editing it a way of
 * speaking to every owner and tenant at once. The router adds a fresh second factor on top.
 *
 * Written separately from the two predicates around it, which it resolves to today, so an agency
 * that wants its marketing person to own the logo without inviting staff finds one rule to change.
 */
export function canEditBranding(roles: readonly string[]): boolean {
  return roles.includes('org_admin') || roles.includes('platform_admin');
}

/**
 * May connect or disconnect the agency's account on a third-party service — Trade Me today.
 *
 * AN ADMINISTRATIVE AUTHORITY, for `canEditBranding`'s reason and a sharper one: a connection is a
 * standing credential that acts publicly in the agency's name, on an account the agency pays for.
 * Connecting the wrong account — a staff member's personal one — is quiet and consequential, so it
 * is not the job of whoever happens to be listing a vacancy. The router adds a fresh second factor.
 *
 * Reading whether one is connected is anybody's: "why isn't the listing up" is asked by the
 * property manager, and "nobody has connected Trade Me" is the answer.
 */
export function canManageIntegrations(roles: readonly string[]): boolean {
  return roles.includes('org_admin') || roles.includes('platform_admin');
}

/**
 * May bind a portal OFFICE to the agency being acted in — realestate.co.nz today (0140).
 *
 * NOT `canManageIntegrations`, and the difference is the whole point. Trade Me is connected with
 * the agency's own sign-in, so the agency proves the account is theirs. realestate.co.nz issues
 * one token to ProManage and links offices to it on its side; nothing an agency can type proves
 * an office is theirs, and an agency that could type another agency's office number could
 * advertise in its name. So the binding is ours to make, at the moment we arrange the link with
 * realestate.co.nz — and it lands in the agency's own audit log like any support action.
 *
 * `isPlatformStaff` is a state no agency can reach by any route it controls (see above).
 */
export function canLinkPortalOffice(roles: readonly string[]): boolean {
  return isPlatformStaff(roles);
}

/** May add, remove and re-role people in the organisation they are acting in. */
export function canManageUsers(roles: readonly string[]): boolean {
  return roles.includes('org_admin') || roles.includes('platform_admin');
}

/** May administer the platform: see every agency, and issue support access into them. */
export function canAdministerPlatform(roles: readonly string[]): boolean {
  return roles.includes('platform_admin');
}

/**
 * May request support access into another organisation.
 *
 * Note this is the right to REQUEST a time-boxed grant, not a right to read anything. The
 * grant creates an ordinary membership that expires; nothing here bypasses RLS, and a support
 * session carries a normal `org_id` like every other session. See docs/SECURITY.md §6.
 */
export function canRequestSupportAccess(roles: readonly string[]): boolean {
  return roles.includes('platform_admin') || roles.includes('support');
}

/**
 * Roles that may maintain the property book — owners, properties, tenancies.
 *
 * `property_manager` is here, and is deliberately NOT a trust role. This is the daily job:
 * signing up a property, starting a tenancy, ending one. Requiring a trust accounting role to
 * record a new tenancy would either stop the work or push every property manager into a role
 * that can move money, and the second is how separation of duties quietly dies.
 *
 * `support` and `platform_admin` are absent. Our staff read; they do not maintain a customer's
 * records. Same line as TRUST_ROLES, for the same reason.
 */
export const PROPERTY_ROLES: readonly Role[] = [
  'property_manager',
  'accounts',
  'trust_admin',
  'org_admin',
];

export function canManageProperties(roles: readonly string[]): boolean {
  return hasAnyRole(roles, PROPERTY_ROLES);
}

/**
 * May CHANGE an ownership split on a property that already exists.
 *
 * Stricter than `canManageProperties`, and the line is worth stating: recording a property's
 * owners at sign-up writes down what the management agreement says. Changing the split
 * afterwards redirects money that is already flowing, to a different person, out of a trust
 * account. That is a money decision wearing a data-entry costume, so it needs a trust role —
 * the same reason a property manager may start a tenancy but not post its rent.
 */
export function canSetOwnership(roles: readonly string[]): boolean {
  return canPostJournals(roles);
}

/**
 * May STRIKE a disbursement run and work it up for approval.
 *
 * The trust roles, which is `accounts` and `trust_admin` — DATA_MODEL.md's table says an
 * `accounts` user proposes disbursements, and this is that line. Proposing moves no money: it
 * reads the ledger, produces a list, and asks somebody else to authorise it. What it does need is
 * a role that is allowed to see every owner's balance, which is why it is not open to a property
 * manager.
 */
export function canProposeDisbursements(roles: readonly string[]): boolean {
  return canPostJournals(roles);
}

/**
 * May AUTHORISE a run, and may PAY one — `trust_admin` alone.
 *
 * NARROWER THAN `canPostJournals`, and this is the separation of duties DATA_MODEL.md calls real.
 * An `accounts` user posts journals all day and proposes the run; the person who says "yes, send
 * $48,120 out of the trust account" is a different role. Collapsing the two would leave §5's
 * proposer-is-not-approver rule resting entirely on two people happening to exist, rather than on
 * the agency having decided who holds the authority.
 *
 * Execution is on the same permission as approval rather than a looser one. It is the act that
 * actually moves the money, and a product where a wider group can execute an approved run has
 * simply moved the authority to the wider group.
 */
export function canApproveDisbursements(roles: readonly string[]): boolean {
  return roles.includes('trust_admin');
}

/**
 * May draw the agency's own commission out of the trust account — `trust_admin` alone.
 *
 * THE SAME LINE AS APPROVING A RUN, and for the same reason rather than by coincidence: this is
 * money leaving the trust bank account, and TRUST_ACCOUNTING.md §1 is explicit that until it is
 * transferred it is still trust money. That the destination is the agency's own operating account
 * does not soften it — a trust account drawn on beyond what has been earned is one owner's money
 * paying the agency, which is invariant 3 again.
 *
 * WRITTEN SEPARATELY FROM `canApproveDisbursements`, which it resolves to today, for the reason
 * that predicate is separate from `canEditPayeeAccounts`: they are three different arguments about
 * who holds an authority, and an agency that ever wants to split them should find three rules to
 * change rather than one predicate used three times.
 *
 * An `accounts` user still POSTS the fee all month — every rent receipt charges it. What they do
 * not do is decide when the agency helps itself to the balance.
 */
export function canTransferCommission(roles: readonly string[]): boolean {
  return roles.includes('trust_admin');
}

/**
 * May record or change where an owner's money is sent — `trust_admin` alone.
 *
 * docs/SECURITY.md §5, and the tightest permission in the product for the best reason: this is the
 * #1 loss event in property management worldwide. It is deliberately NOT `canManageProperties`,
 * even though a payee account looks like a field on an owner record — the same argument
 * `canSetOwnership` makes, one step further. Recording an owner's address is data entry; recording
 * where their money goes is a payment instruction wearing a data-entry costume.
 *
 * It is also not `canPostJournals`. An `accounts` user who could redirect a payee and then propose
 * the run that pays it has both halves of the attack, and the 24-hour quarantine would be the only
 * thing left standing.
 */
export function canEditPayeeAccounts(roles: readonly string[]): boolean {
  return roles.includes('trust_admin');
}

/**
 * May bring an agency's opening balances across from its previous system — `trust_admin` alone.
 *
 * It posts the opening balance of every owner in the trust account at once, it happens once in an
 * agency's life, and it is undone only by reversing journals. The same authority as approving a
 * run, and the server's `trustAdminProcedure` draws the same line.
 */
export function canCutOver(roles: readonly string[]): boolean {
  return roles.includes('trust_admin');
}

/**
 * May pay a tenancy's bond back OUT of the trust account — `trust_admin` alone.
 *
 * The line `bond.refund` and `bond.lodge` already sit behind on the server (`trustAdminProcedure`).
 * Named here for the same reason `canTransferCommission` is named separately from
 * `canApproveDisbursements` although both resolve to the role today: this is its own argument
 * about who holds an authority. A bond is a tenant's money that the agency is holding to a
 * statutory deadline, and paying it back to the wrong person is not a posting error somebody
 * reverses — it is money gone, with a Tenancy Services obligation still outstanding behind it.
 *
 * RECEIPTING a bond is deliberately NOT behind this. Money arriving is `canPostJournals`, exactly
 * as rent is; it is the drawing that is the authority.
 *
 * It hides a control that would otherwise refuse the operator on every draft in the queue. It
 * withholds nothing — the procedure is where the refusal actually lives.
 */
export function canRefundBond(roles: readonly string[]): boolean {
  return roles.includes('trust_admin');
}

/**
 * May hand rent back to the tenant who paid it — `trust_admin` alone.
 *
 * The line `rent.refund` sits behind on the server. Written separately from `canRefundBond`,
 * which it resolves to today, because they are two different arguments: a bond is money the agency
 * was only ever HOLDING, where refunded rent has already been credited to an owner and had a fee
 * charged on it. Handing it back takes money out of a beneficiary's balance and out of the
 * agency's own income, which is nearer to `canTransferCommission` than to returning a deposit.
 *
 * RECEIPTING rent is `canPostJournals` and stays there. An `accounts` user takes the money in all
 * day; deciding to send some of it back out is the authority docs/SECURITY.md §5 keeps separate.
 */
export function canRefundRent(roles: readonly string[]): boolean {
  return roles.includes('trust_admin');
}

/**
 * May close a month, and may reopen one — a trust role AND the agency's explicit grant.
 *
 * A PERMISSION THE AGENCY SETS, per person (`memberships.may_close_periods`, 0124), because who
 * signs the trust account off is the agency's governance question rather than ours: one agency has
 * a principal who signs every month, another lets its senior accounts person do it. Hard-coding
 * `trust_admin` answered that question for every customer at once.
 *
 * THE GRANT ALONE IS NOT ENOUGH. Closing a month is a statement about every beneficiary's balance,
 * so it stays inside `TRUST_ROLES` — the people who can already see and move that money. A grant
 * left on a membership whose trust role is later withdrawn simply stops counting, rather than
 * needing a second edit somebody will forget.
 *
 * The same line `lockIssues` and `unlockIssues` enforce in `period.ts`, named here so a screen can
 * ask the question in the words the server answers it in.
 */
export function canClosePeriods(roles: readonly string[], granted: boolean): boolean {
  return granted && hasAnyRole(roles, TRUST_ROLES);
}

/**
 * Whether a NEW membership starts with the close permission.
 *
 * `trust_admin` does, because that is who closed months before the permission existed and an
 * agency inviting its trust administrator expects them to be able to. Everyone else starts without
 * it and is granted it deliberately. 0124 backfilled existing memberships the same way.
 */
export function defaultMayClosePeriods(roles: readonly string[]): boolean {
  return roles.includes('trust_admin');
}

export interface ClosePermissionChangeRequest {
  actorUserId: string;
  targetUserId: string;
  /** The target's roles — the grant is only offered to someone who could use it. */
  targetRoles: readonly string[];
  grant: boolean;
}

/**
 * Problems with granting or withdrawing someone's permission to close months.
 *
 * NOT ON YOURSELF. An administrator who also holds a trust role could otherwise grant themselves
 * the sign-off and close a month with nobody else involved — the same reason whoever closed a month
 * may not be the one to reopen it.
 */
export function closePermissionChangeIssues(request: ClosePermissionChangeRequest): string[] {
  const issues: string[] = [];

  if (request.actorUserId === request.targetUserId) {
    issues.push('Another administrator must change your own permission to close months');
  }

  if (request.grant && !hasAnyRole(request.targetRoles, TRUST_ROLES)) {
    issues.push(
      'Only someone with a trust accounting role (Accounts or Trust admin) can be allowed to close months',
    );
  }

  return issues;
}

/**
 * Whose screens LEAD with the agency's whole trust position — `trust_admin` alone.
 *
 * A PRESENTATION RULE, NOT A CONTROL, and that distinction has to stay visible or this function
 * becomes a lie. `ledger.position` and `dashboard.summary` are `verifiedProcedure`s: the server
 * serves these totals to any signed-in member and should keep doing so — an `accounts` user
 * posting a correction needs to see what the books say, and the reconciliation strip is built on
 * the same figures. What this decides is whether a card summing every owner's money is what a
 * screen OPENS with, which for a property manager it is not. Their job is a property; the
 * agency's consolidated trust position is neither theirs to act on nor useful to them.
 *
 * So it hides a card. It withholds nothing, and it must never be asked to — a real restriction
 * belongs on the procedure, where a request cannot route around it. See the note at the top of
 * apps/web `features/auth/gate.ts`.
 */
export function canViewTrustPosition(roles: readonly string[]): boolean {
  return roles.includes('trust_admin');
}

/**
 * Who may READ the trust account's own figures — the consolidated position, the reconciliation
 * and the monthly reconciliation report. A CONTROL: `trustReaderProcedure` in the API enforces it.
 *
 * `TRUST_ROLES`, because those figures are the trust accountants' work: every owner's money
 * summed, and the proof that it matches the bank. A property manager's job is a property, and
 * the per-owner balances and the transactions they need stay open to them.
 *
 * PLUS `support`, the same call `AUDIT_READ_ROLES` makes and for the same reason: "this figure is
 * wrong and nobody here knows why" is the usual support call, and our staff can read a customer's
 * books but never touch them — TRUST_ROLES still refuses every write. Each support session is in
 * the customer's own audit log, which is the control.
 */
export const TRUST_READ_ROLES: readonly Role[] = [...TRUST_ROLES, 'support'];

export function canReadTrustAccount(roles: readonly string[]): boolean {
  return hasAnyRole(roles, TRUST_READ_ROLES);
}

/**
 * Who sees RECONCILE at all — the rail item and its four screens. Exactly who may read what they
 * show, so the section is never a page of refusals: `canReadTrustAccount`.
 */
export function canOpenReconcile(roles: readonly string[]): boolean {
  return canReadTrustAccount(roles);
}

/**
 * Who may open the FINANCE dashboard — `accounts` and `trust_admin`, the trust accounting roles.
 *
 * A CONTROL, UNLIKE `canViewTrustPosition` ABOVE. That one decides what a screen leads with and
 * withholds nothing. This one is enforced on `dashboard.summary` itself, so a property manager who
 * types `/` into the address bar, or calls the procedure directly, is refused rather than shown a
 * page with a card missing. The finance overview is the agency's whole trust position in one read —
 * the three-way, every beneficiary balance, what is unmatched — and the agency has decided that is
 * the trust accountants' view, not everybody's.
 *
 * `TRUST_ROLES` rather than a list of its own, so it moves with who may post journals: the people
 * who keep the books are the people who read the books' overview. `org_admin` is not in it —
 * administering staff is not keeping the trust account — and an administrator who does both holds
 * `trust_admin` as well, which is how an agency is set up (`DEFAULT_INITIAL_ADMIN_ROLES`).
 *
 * Everybody else opens the property management overview, which is portfolio-scoped and holds no
 * agency-wide trust figure.
 */
export function canViewFinanceDashboard(roles: readonly string[]): boolean {
  return hasAnyRole(roles, TRUST_ROLES);
}

/**
 * The overview a person's Dashboard actually opens on: their saved choice, unless they may not
 * open it. A preference is not a permission — a stored `finance` outlives a role change, and every
 * account defaulted to `finance` when the column arrived (0122).
 */
export function effectiveDashboardLayout(
  roles: readonly string[],
  stored: DashboardLayout,
): DashboardLayout {
  return stored === 'finance' && !canViewFinanceDashboard(roles) ? 'management' : stored;
}

/** The overviews this person may choose between, in menu order. */
export function availableDashboardLayouts(roles: readonly string[]): DashboardLayout[] {
  // Custom for everybody: what goes ON it is filtered per widget — see `availableWidgets`.
  return canViewFinanceDashboard(roles)
    ? ['finance', 'management', 'custom']
    : ['management', 'custom'];
}

/**
 * Roles that may read the agency's audit log.
 *
 * NARROWER THAN "anyone who works here", and narrower than `PROPERTY_ROLES`. The log is not a
 * list of property events — it carries every sign-in, every role change, every time our own
 * staff were let in, and the ip address each of those came from. That is a record ABOUT the
 * agency's staff, and a property manager reading their colleagues' login history is a privacy
 * problem the agency did not ask us to create for them.
 *
 * `accounts` and `trust_admin` are here because the audit log is trust evidence: the people who
 * sign off a month and face the auditor are the people who need to produce it. `org_admin` is
 * here because the access half of the log is exactly what they administer.
 *
 * `support` IS here, and that is the one entry worth defending. It reads — our staff cannot
 * write to a customer's books at all (TRUST_ROLES, PROPERTY_ROLES) — and the usual support call
 * is "something happened to this figure and nobody here knows what". Refusing our own staff the
 * log would mean answering that by asking the customer to read it out. Every support session is
 * itself in the log the customer can read, under its own category, which is the control that
 * makes this safe rather than the absence of the permission.
 */
export const AUDIT_READ_ROLES: readonly Role[] = [
  'accounts',
  'trust_admin',
  'org_admin',
  'support',
  'platform_admin',
];

export function canReadAuditLog(roles: readonly string[]): boolean {
  return hasAnyRole(roles, AUDIT_READ_ROLES);
}

// ── Support access grants ────────────────────────────────────────────────────

/** Default window for a support grant. Long enough for a call, short enough to forget. */
export const SUPPORT_GRANT_DEFAULT_MS = 2 * 60 * 60 * 1000;

/**
 * Hard ceiling. "No standing production access" (docs/SECURITY.md §6) means an access grant
 * has to expire on a timescale a customer would recognise as an incident, not a tenancy.
 */
export const SUPPORT_GRANT_MAX_MS = 8 * 60 * 60 * 1000;

export const MIN_SUPPORT_REASON_LENGTH = 10;

/**
 * Problems with a proposed support grant. Empty means acceptable.
 *
 * The reason is mandatory and has a length floor because this text is what the CUSTOMER sees
 * in their own audit log. "asdf" is not an explanation to someone asking why a stranger read
 * their trust account.
 */
export function supportGrantIssues(reason: string, durationMs: number): string[] {
  const issues: string[] = [];

  if (reason.trim().length < MIN_SUPPORT_REASON_LENGTH) {
    issues.push(
      `Give a reason of at least ${MIN_SUPPORT_REASON_LENGTH} characters — the customer sees it`,
    );
  }
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    issues.push('Access must last a positive amount of time');
  }
  if (durationMs > SUPPORT_GRANT_MAX_MS) {
    issues.push(`Access cannot exceed ${SUPPORT_GRANT_MAX_MS / (60 * 60 * 1000)} hours`);
  }

  return issues;
}

// ── Opening an agency ────────────────────────────────────────────────────────

/**
 * Setting up a new customer — the one act a platform administrator performs ON an agency
 * rather than inside one.
 *
 * IT CREATES NO ACCESS FOR US. The organisation, its system ledger accounts and its first
 * administrator are written by a `security definer` function; the platform user gets no
 * membership out of it, so the moment the agency exists it is already a place we cannot read.
 * Getting in afterwards is `grantSupportAccess` with a reason the customer sees, exactly as it
 * is for an agency we did not set up — which is the whole point of provisioning this way rather
 * than by making ourselves a member and remembering to leave.
 *
 * The one thing that stays open is re-sending the first sign-in link, and it closes by itself:
 * see `canReissueSetupLink`.
 */

export const MIN_ORG_NAME_LENGTH = 2;
export const MAX_ORG_NAME_LENGTH = 120;

/**
 * The role the first user MUST hold.
 *
 * Not a default somebody may clear. An agency whose only member cannot invite anybody has
 * nobody who can add the second, and recovering from that means us in their database — the
 * access this whole design exists to make rare. It is the same failure `roleChangeIssues`
 * guards at the other end, one step earlier.
 */
export const INITIAL_ADMIN_ROLE: Role = 'org_admin';

/**
 * What the first user is offered, before anybody changes it.
 *
 * A trust role as well as the administrator one, because the first person at a small agency is
 * the person who records the trust account and receipts the first rent — and neither is
 * something `org_admin` may do. Starting them without it would mean their first act is to
 * re-role themselves, which teaches that roles are paperwork.
 */
export const DEFAULT_INITIAL_ADMIN_ROLES: readonly Role[] = ['org_admin', 'trust_admin'];

export interface OrganisationSetupRequest {
  /** The agency's name, as it will appear to its own staff and on its owners' statements. */
  name: string;
  /** The first user's full name. Their email is parsed at the edge — see contracts. */
  adminName: string;
  roles: readonly string[];
}

/**
 * Problems with a proposed agency. Empty means acceptable.
 *
 * Same shape and same reasoning as `supportGrantIssues`: the form runs it for a fast answer,
 * the API runs it because a form is not a control, and the database restates the two rules that
 * matter (an assignable role, and an administrator among them) because a function neither of
 * them wrote could still call it.
 */
export function organisationSetupIssues(request: OrganisationSetupRequest): string[] {
  const issues: string[] = [];
  const name = request.name.trim();

  if (name.length < MIN_ORG_NAME_LENGTH) {
    issues.push('Give the agency its name — it is what its owners see on every statement');
  } else if (name.length > MAX_ORG_NAME_LENGTH) {
    issues.push(`The name cannot be longer than ${MAX_ORG_NAME_LENGTH} characters`);
  }

  if (request.adminName.trim().length === 0) {
    issues.push("Give the first user's name");
  }

  if (request.roles.length === 0) {
    issues.push('Choose at least one role for the first user');
  }

  for (const role of request.roles) {
    if (!(ROLES as readonly string[]).includes(role)) {
      issues.push(`Unknown role: ${role}`);
    } else if (!AGENCY_ASSIGNABLE_ROLES.includes(role as Role)) {
      // `support` and `platform_admin` are ours. An agency holding one would be an agency that
      // can issue itself access into other agencies — see PLATFORM_ONLY_ROLES.
      issues.push(`${role} cannot be assigned in an agency`);
    }
  }

  if (!request.roles.includes(INITIAL_ADMIN_ROLE)) {
    issues.push('The first user must be an administrator — nobody else can add the second');
  }

  return issues;
}

/**
 * Whether we may still re-send the first sign-in link into an agency we opened.
 *
 * THE DOOR THAT SHUTS ON ITS OWN. A setup link expires in a week and an address can be typed
 * wrong, so an agency whose first administrator never got in would otherwise be unreachable
 * without a support grant — and `support` cannot administer users, so the grant would not even
 * fix it. This is the narrow answer: re-issue the token, to the person we already created, for
 * as long as nobody in that agency has ever signed in.
 *
 * It closes at the FIRST LOGIN and never reopens. From then on the agency has somebody who can
 * invite, unlock and re-role its own staff, and anything we do there goes through a support
 * grant with a reason in their audit log. `hasLoggedIn` is a fact about the agency's own
 * members, never about ours — a support session signing in must not reopen it.
 */
export function canReissueSetupLink(agency: {
  /** True once any of the agency's own (non-support) members has signed in, ever. */
  hasLoggedIn: boolean;
  /** True while the first user still has not set a password. */
  firstUserAwaitingSetup: boolean;
}): boolean {
  return !agency.hasLoggedIn && agency.firstUserAwaitingSetup;
}

// ── Membership changes ───────────────────────────────────────────────────────

export interface MembershipSummary {
  userId: string;
  roles: readonly string[];
  status: 'active' | 'suspended';
  /** True when this membership was issued as support access from the platform org. */
  isSupport: boolean;
}

export interface RoleChangeRequest {
  actorUserId: string;
  targetUserId: string;
  nextRoles: readonly string[];
  orgKind: OrgKind;
  /** Every membership in the organisation, as it stands right now. */
  current: readonly MembershipSummary[];
}

/**
 * Problems with a proposed role change. Empty means allowed.
 *
 * The two rules worth stating out loud:
 *
 * - **An administrator cannot demote themselves.** Not paternalism: it is the difference
 *   between one misclick and an agency with nobody who can add users back.
 * - **The last administrator cannot be demoted or suspended by anyone.** Same failure, one
 *   step removed. Recovering from it needs us, in their database, which is precisely the
 *   access we have designed to be rare.
 */
export function roleChangeIssues(request: RoleChangeRequest): string[] {
  const issues: string[] = [];
  const { actorUserId, targetUserId, nextRoles, orgKind, current } = request;

  const target = current.find((m) => m.userId === targetUserId);
  if (!target) return ['That person is not a member of this organisation'];

  if (target.isSupport) {
    // Support memberships are issued and revoked by the platform, with their own audit trail.
    // An agency admin editing one would break that record.
    return ['Support access is managed by ProManage and cannot be edited here'];
  }

  if (nextRoles.length === 0) {
    issues.push('Choose at least one role — remove the person instead if they should have none');
  }

  const allowed = assignableRoles(orgKind);
  for (const role of nextRoles) {
    if (!(ROLES as readonly string[]).includes(role)) {
      issues.push(`Unknown role: ${role}`);
    } else if (!allowed.includes(role as Role)) {
      issues.push(`${role} cannot be assigned in this organisation`);
    }
  }

  const losesAdmin = target.roles.includes('org_admin') && !nextRoles.includes('org_admin');

  if (losesAdmin && actorUserId === targetUserId) {
    issues.push('You cannot remove your own administrator role');
  }

  if (losesAdmin && isLastAdmin(targetUserId, current)) {
    issues.push('This is the last administrator — promote someone else first');
  }

  return issues;
}

export interface StatusChangeRequest {
  actorUserId: string;
  targetUserId: string;
  nextStatus: 'active' | 'suspended';
  current: readonly MembershipSummary[];
}

export function statusChangeIssues(request: StatusChangeRequest): string[] {
  const { actorUserId, targetUserId, nextStatus, current } = request;

  const target = current.find((m) => m.userId === targetUserId);
  if (!target) return ['That person is not a member of this organisation'];

  if (target.isSupport) {
    return ['Support access is managed by ProManage and cannot be edited here'];
  }

  const issues: string[] = [];

  if (nextStatus === 'suspended') {
    if (actorUserId === targetUserId) {
      issues.push('You cannot suspend yourself');
    }
    if (target.roles.includes('org_admin') && isLastAdmin(targetUserId, current)) {
      issues.push('This is the last administrator — promote someone else first');
    }
  }

  return issues;
}

/** True when nobody else in the org is an active, non-support administrator. */
function isLastAdmin(userId: string, current: readonly MembershipSummary[]): boolean {
  return !current.some(
    (m) =>
      m.userId !== userId && m.status === 'active' && !m.isSupport && m.roles.includes('org_admin'),
  );
}

// ── Portfolio scope ──────────────────────────────────────────────────────────

/**
 * How much of the agency's book a person is looking at.
 *
 * `own` is the properties they are named on in `property_managers`, and everything that hangs
 * off those. `agency` is the whole company. Two values and no middle: "my team's portfolio"
 * needs a team, and inventing one here would mean guessing at an org chart we do not hold.
 */
export const PORTFOLIO_SCOPES = ['own', 'agency'] as const;
export type PortfolioScope = (typeof PORTFOLIO_SCOPES)[number];

export function isPortfolioScope(value: string): value is PortfolioScope {
  return (PORTFOLIO_SCOPES as readonly string[]).includes(value);
}

/**
 * Roles whose job IS the whole book, whatever the membership column says.
 *
 * Not a convenience: an `accounts` user strikes a disbursement run across every owner
 * (`canProposeDisbursements`), a `trust_admin` signs off a three-way reconciliation of one bank
 * account holding every beneficiary's money, and neither is answerable from a slice. Narrowing
 * them would not be a preference, it would produce a trust position that does not balance —
 * which is invariant 2 with the evidence removed rather than a smaller screen.
 *
 * `org_admin` is here because they administer the agency, and `support` and `platform_admin`
 * because our own staff arrive to answer "where did this figure come from" and hold no property
 * assignments at all — scoped to `own` they would see an empty console.
 */
export const WHOLE_PORTFOLIO_ROLES: readonly Role[] = [
  'accounts',
  'trust_admin',
  'org_admin',
  'support',
  'platform_admin',
];

/** True when the roles alone settle it and the granted column has nothing to say. */
export function portfolioScopeIsInherent(roles: readonly string[]): boolean {
  return hasAnyRole(roles, WHOLE_PORTFOLIO_ROLES);
}

/**
 * The WIDEST scope this membership may see — the permission itself.
 *
 * Two inputs, because they answer different questions. The roles say whether the whole book is
 * inherent to the job; `granted` is what the agency's administrator has decided for a person
 * whose job it is not. A property manager is `own` until somebody grants them `agency`.
 *
 * The server clamps every request through this. Nothing else may.
 */
export function permittedPortfolioScope(
  roles: readonly string[],
  granted: PortfolioScope,
): PortfolioScope {
  return portfolioScopeIsInherent(roles) ? 'agency' : granted;
}

/**
 * Whether this person is offered the toggle at all.
 *
 * A PRESENTATION RULE, like `canViewTrustPosition`, and it hides a control rather than
 * withholding anything: somebody permitted only `own` who asks for `agency` is not refused, they
 * are answered with their own portfolio — see `effectivePortfolioScope`. The refusal that
 * matters lives in the query, where a request cannot route around it.
 */
export function canTogglePortfolioScope(
  roles: readonly string[],
  granted: PortfolioScope,
): boolean {
  return permittedPortfolioScope(roles, granted) === 'agency';
}

/**
 * What a request actually gets: the narrower of what they may see and what they asked for.
 *
 * CLAMPS RATHER THAN REFUSES, which is the opposite of how this codebase treats a client-supplied
 * `org_id` — and the difference is worth stating. A tenant scope arriving from the client is an
 * attempt to act as another agency, so it is rejected outright (REJECT_CLIENT_ORG_ID). A view
 * scope is a preference held in the browser across reloads, and the honest answer to "show me
 * everything" from someone who may not is their own portfolio, not an error page they cannot
 * clear. Nothing widens here: `requested` can only ever narrow what `permitted` already allows.
 */
export function effectivePortfolioScope(
  permitted: PortfolioScope,
  requested: PortfolioScope | undefined,
): PortfolioScope {
  if (permitted === 'own') return 'own';
  return requested ?? 'agency';
}

/**
 * What a newly invited member starts with.
 *
 * `own` for anyone whose roles do not already carry the whole book — least privilege, and the
 * grant is a deliberate act by an administrator rather than something a new starter arrives
 * holding. Existing memberships were backfilled to `agency` by `0059_portfolio_scope.sql`: an
 * agency that has been running for a year must not find eighty per cent of its book hidden from
 * its staff on the morning we deploy this.
 */
export function defaultPortfolioScope(roles: readonly string[]): PortfolioScope {
  return portfolioScopeIsInherent(roles) ? 'agency' : 'own';
}

export interface PortfolioScopeChangeRequest {
  actorUserId: string;
  targetUserId: string;
  nextScope: PortfolioScope;
  current: readonly MembershipSummary[];
}

/**
 * Problems with a proposed scope change. Empty means allowed.
 *
 * Deliberately thin next to `roleChangeIssues`, because the failure it guards against is not the
 * same: nobody locks an agency out of its own account by narrowing what one person browses. The
 * one rule is the support one — a membership ProManage issued is ours to configure, and an agency
 * editing it would break the record of what our staff could see.
 */
export function portfolioScopeChangeIssues(request: PortfolioScopeChangeRequest): string[] {
  const { targetUserId, nextScope, current } = request;

  const target = current.find((m) => m.userId === targetUserId);
  if (!target) return ['That person is not a member of this organisation'];

  if (target.isSupport) {
    return ['Support access is managed by ProManage and cannot be edited here'];
  }

  if (!isPortfolioScope(nextScope)) return [`Unknown portfolio scope: ${nextScope as string}`];

  // Not an error, but saying so is better than a control that appears to do nothing: their roles
  // already carry the whole book, so the column they are setting will never be consulted.
  if (portfolioScopeIsInherent(target.roles) && nextScope === 'own') {
    return ['Their role already covers the whole agency — remove it first to narrow what they see'];
  }

  return [];
}
